import { useState, useEffect, useRef } from 'react';
import Plan from './plan';
import PlansDescriptor from './plans_descriptor';
import MagnificPopup from './magnific_popup';
import { createRoot } from 'react-dom/client';
import ComparisonTable from './comparison_table';
import filesize from 'filesize';
import CurrencySwitcher from './currency_switcher';
import Tooltip from './tooltip';

const Pricing = ({ startUrl, startSettingsUrl, plansUrl }) => {
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState(I18n.locale == 'cs' ? 'CZK' : 'EUR');
  const [plans, setPlans] = useState([]);
  const [hoveredFeatureIndex, setHoveredFeatureIndex] = useState(undefined);
  const [buttonMinWidth, setButtonMinWidth] = useState(0)
  const button2Ref = useRef(null);

  const features = [
    {
      name: I18n.t('pricing.limits-users'),
      kind: 'users',
    },
    {
      name: I18n.t('pricing.all-caflou-modules'),
      kind: 'fixed',
    },
    {
      name: I18n.t('pricing.process-automation'),
      kind: 'automation',
    },
    {
      name: I18n.t('pricing.api-integration'),
      kind: 'api',
    },
    {
      name: I18n.t('pricing.storage-size'),
      kind: 'storage',
    },
    {
      name: I18n.t('pricing.consultation'),
      hint: I18n.t('pricing.support-items.consultations-hint'),
      kind: 'consultation',
    },
    {
      name: I18n.t('pricing.private-cloud'),
      hint: I18n.t('pricing.private-cloud-hint'),
      kind: 'private_cloud',
    },
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (button2Ref.current) setButtonMinWidth(button2Ref.current.offsetWidth)
    }, 100)
  }, [loading])

  const fetchPlans = () => {
    jQuery.ajax({
      url: plansUrl,
      success: (data) => {
        if (data.plans) {
          setPlans(data.plans);
          setLoading(false);
        }
      },
    });
  };

  const handleCurrencyChange = (e) => {
    if (I18n.locale == 'cs') {
      if (e.target.checked) {
        setCurrency('EUR');
      } else {
        setCurrency('CZK');
      }
    } else {
      if (e.target.checked) {
        setCurrency('USD');
      } else {
        setCurrency('EUR');
      }
    }
  };

  const handleMouseOut = (e) => {
    if (!e.target.closest('.feature, .feature-label')) {
      setHoveredFeatureIndex(undefined);
    }
  };

  const setComparisonTableVisible = () => {
    const comparisonTable = <ComparisonTable plans={plans} />;
    if (window.reactMagnificPopup) {
      window.reactMagnificPopup.setState({
        visible: true,
        content: comparisonTable,
      });
    } else {
      const magnific_popup_dom = document.getElementById(
        'react-magnific-popup'
      );
      if (magnific_popup_dom) {
        const root = createRoot(magnific_popup_dom);
        root.render(
          React.createElement(MagnificPopup, {
            content: comparisonTable,
            visible: true,
          })
        );
      }
    }
  };

  let supportEmail = 'support@caflou.com';
  if (I18n.locale == 'cs') {
    supportEmail = 'podpora@caflou.cz';
  }

  let defaultPlan = plans.find((i) => i.default == true);

  if (loading) {
    return (
      <div className='text-center' style={{ marginBottom: '24px' }}>
        <div className='progress-spinner-icon dark'></div>
      </div>
    );
  }

  return (
    <div className='pricing' onMouseOut={handleMouseOut}>
      <div className='plans-wrapper-1' onMouseOut={handleMouseOut}>
        <div className='plans-wrapper-3'>
          <PlansDescriptor
            {...{
              features,
              hoveredFeatureIndex,
              setHoveredFeatureIndex,
              setComparisonTableVisible,
            }}
          />
          {/* <CurrencySwitcher handleCurrencyChange={handleCurrencyChange} /> */}
          {/* <div className='note'>
            {I18n.t('pricing-without-vat')}
          </div> */}
        </div>

        <div className='plans-wrapper-2'>
          <div className='plans-wrapper-4'>
            <div className='new-plans'>
              {plans
                .filter((i) =>
                  ['Pstruh', 'Kapr', 'Delfín', 'Žralok'].includes(i.name_cs)
                )
                .map((plan, index) => (
                  <Plan
                    {...{
                      plan,
                      index,
                      supportEmail,
                      features,
                      currency,
                      hoveredFeatureIndex,
                      setHoveredFeatureIndex,
                      startSettingsUrl,
                    }}
                    key={plan.id}
                  />
                ))}
            </div>
          </div>
          <CurrencySwitcher handleCurrencyChange={handleCurrencyChange} />
          <div className="note">
            {I18n.t('pricing-without-vat')}{' '}{I18n.t('pricing.note-bonus')}
          </div>
        </div>
      </div>

      {defaultPlan ? (
        <div className='order-flow'>
          <div className='step plan'>
            <div className='title'>
              <strong className='green'>
                {defaultPlan[`name_${I18n.locale}`]}
              </strong>
              {' - '}
              <span style={{ fontWeight: 'normal' }}>
                {defaultPlan[`description_${I18n.locale}`]}
              </span>
              <p>
                {I18n.t('number-of-users')} ({defaultPlan.limits_users}) •{' '}
                {I18n.t('all-caflou-modules')} ({defaultPlan.limits_objects}{' '}
                {I18n.t('objects-count', { count: defaultPlan.limits_objects })}
                ) • API ({defaultPlan.limits_api_calls}{' '}
                {`${I18n.t('api-request-count', {
                  count: defaultPlan.limits_api_calls,
                })}/${I18n.t('month-count', { count: 1 })}`}
                )
                <br />• {I18n.t('workflow-management')} (
                {defaultPlan.limits_workflow_causes}{' '}
                {I18n.t('process-count', {
                  count: defaultPlan.limits_workflow_causes,
                })}
                ) • {I18n.t('files')} (
                {
                  filesize(defaultPlan.limits_data * 1.0e9, {
                    output: 'object',
                  }).value
                }{' '}
                {
                  filesize(defaultPlan.limits_data * 1.0e9, {
                    output: 'object',
                  }).symbol
                }
                )
              </p>
            </div>
            <div className='selection'>
              <a
                href={startUrl}
                className='btn btn-primary'
                style={{ minWidth: buttonMinWidth }}
              >
                {I18n.t('activate')}
              </a>
            </div>
          </div>
        </div>
      ) : null}

      <div className='order-flow'>
        <div className='step plan'>
          <div className='title'>
            <strong className='green'>{I18n.t('pricing.custom-plan')}</strong>
            {' - '}
            <span style={{ fontWeight: 'normal' }}>
            {I18n.t('pricing.custom-subtitle')}{' '}
              <Tooltip
                content={I18n.t('pricing.custom-hint')}
                placement='right'
              >
                <i className='fal fa-question-circle'></i>
              </Tooltip>
            </span>
            <p>{I18n.t('pricing.custom-plan-text')} </p>
          </div>
          <div className='selection'>
            <a
              href={`mailto:${supportEmail}?subject=${I18n.t('plan-interest', {
                name: I18n.t('pricing.custom-plan'),
              })}`}
              className='btn btn-primary'
              ref={button2Ref}
            >
              {I18n.t('i-am-interested')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
